import React from "react";
import PropTypes from "prop-types";
import { Route, Switch, Redirect, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "components/ui/loader";
import LazyWrapper from "components/ui/lazy-wrapper";
import Template from "components/template/template";
import { getOneOrganization } from "actions/api/organization";
import { RouterContext } from "../../context/routerContext";
import OrganizationContext from "../../context/OrganizationContext";
import { SelectedOptionProvider } from "pages/templates/templateDetails/components/leftContainer/contexte/TemplateContext";
import {
	GoFastExportProvider,
	PollingProvider,
	ValidationStepProvider
} from "@weezevent/gofast-front";
import "@weezevent/gofast-front/dist/index.css";
import { GoFastExportNotifications } from "../GoFastExportNotifications/GoFastExportNotifications";
import { useLocaleStore } from "../../store/locale";
import CheckpointsView from "pages/checkpoint/CheckpointsView";
import AttendeesList from "../../pages/attendees-list";
import { EventConfigList } from "../../pages/event-config/event-config-list/event-config-list";
import CapacityMiddleware from "./capacity-middleware";
import UnauthorizedPage from "../../pages/error/unauthorized-page";
import { PermissionsContext } from "../user/permissions";

const EventConfigRouter = React.lazy(() => import("./event-config-router"));

const EditCheckpointView = React.lazy(() =>
	import("pages/checkpoint/editCheckpointView")
);
const PartnerList = React.lazy(() => import("pages/partners/partner-list"));
const IntegrationsView = React.lazy(() => import("pages/integrations"));
const CustomScreens = React.lazy(() => import("pages/custom-screens"));
const DeviceStatusList = React.lazy(() =>
	import("pages/device-status/device-status-list")
);
const EditIntegrationView = React.lazy(() =>
	import("pages/integrations/editview")
);
const ManualView = React.lazy(() => import("pages/checkpoint/manual/view"));
const ReportView = React.lazy(() => import("pages/report/view"));

const CheckpointView = React.lazy(() =>
	import("pages/checkpoint-v2/pages/checkpoint-view")
);
const EquipmentContainer = React.lazy(() =>
	import("pages/exploitation/equipment/equipmentContainer")
);
const DevicesSolutions = React.lazy(() => import("pages/devices/solutions"));
const Wap = React.lazy(() => import("pages/devices/wap"));
const Home = React.lazy(() => import("pages/home/"));
const SettingsView = React.lazy(() => import("pages/settings"));
const AttendeeDetails = React.lazy(() =>
	import("../../pages/attendee-details")
);
const ScansStatistics = React.lazy(() =>
	import("../../pages/scans-statistics")
);

const TemplateDetails = React.lazy(() =>
	import("../../pages/templates/templateDetails/templateDetails")
);
const TemplatesList = React.lazy(() =>
	import("../../pages/templates/templatesList/templatesList")
);
const OnEventConfigOnboarding = React.lazy(() =>
	import(
		"pages/event-config/pages/event-config-detail/on-event-config-on-boarding"
	)
);
const EventConfigPartner = React.lazy(() =>
	import(
		"pages/event-config/pages/event-config-partners/event-config-partner"
	)
);
const ExportPage = React.lazy(() => import("pages/export-page"));
const ExternalIntegrationEditView = React.lazy(() =>
	import("pages/integrations/external-integration-edit-view")
);
const NotFoundPage = React.lazy(() => import("pages/error/not-found-page"));

const selectOrganization = state => state.organization;

const OrganizationRoutes = ({ path, organization }) => {
	const history = useHistory();
	const locale = useLocaleStore();

	return (
		<GoFastExportProvider
			baseUrl={window.weezConfig.api.apiUrl}
			endpoint={`/organizations/${organization.id()}/export`}
			locale={locale}
		>
			<PollingProvider>
				<ValidationStepProvider>
					<GoFastExportNotifications />
					<div id="pusher">
						<Switch>
							<Route
								exact
								path={`${path}`}
								render={() => (
									<Redirect to={`${path}/checkpoints`} />
								)}
							/>
							<Route
								path={`${path}/E:eventId(\\d+)`}
								render={routeProps => (
									<LazyWrapper>
										<EventConfigRouter
											orgaPath={path}
											organization={organization}
											{...routeProps}
										/>
									</LazyWrapper>
								)}
							/>
							<Route
								// This route is theorically /home. But now the default route is /checkpoints
								// cf AXP-281 (so if there is still links to /home they will redirect ;) )
								// git grep AXP-281 to find all related changes or commented code.
								path={`${path}/home-wip`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<Home
													{...routeProps}
													organization={organization}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/partners`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<PartnerList
													organization={organization}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/attendees`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<AttendeesList
												{...routeProps}
												organization={organization}
											/>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								path={`${path}/attendees/:id`}
								exact
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<AttendeeDetails
													{...routeProps}
													organization={organization}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/partners/new`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<EventConfigPartner
													organization={organization}
													eventId={null}
													isEditMode={false}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/partners/edit/:partnerId`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<EventConfigPartner
													organization={organization}
													eventId={null}
													isEditMode={true}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/checkpoints`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<CheckpointsView
												organization={organization}
												{...routeProps}
											/>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/checkpoints/:checkpointId/manual`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<ManualView />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/checkpoints/new`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<EditCheckpointView />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/checkpoints/edit/:checkpointId`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<EditCheckpointView />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/checkpoints/new-v2`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<CheckpointView
													organization={organization}
													history={history}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/checkpoints/edit-v2/:checkpointId`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<CheckpointView
													organization={organization}
													history={history}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/events/new`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
										hideMenu={true}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<OnEventConfigOnboarding
													organization={organization}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/events`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<EventConfigList orgaPath={path} />
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<SettingsView />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation/report`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<ReportView />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation/scans-statistics`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<ScansStatistics />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation/devices/wap`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<Wap />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation/devices`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<DevicesSolutions
													organization={organization}
													{...routeProps}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation/equipments`}
								component={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<EquipmentContainer
													organization={organization}
													{...routeProps}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation/integrations/new`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<ExternalIntegrationEditView />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation/integrations/:type/:integrationId(\\d+)`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<ExternalIntegrationEditView />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/exploitation/integrations`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<IntegrationsView
													organization={organization}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								path={`${path}/exploitation/integrations-old`}
								render={routeProps => (
									<Switch>
										<Route
											exact
											path={`${path}/exploitation/integrations-old/new/:type`}
											render={routeProps => (
												<Template
													organization={organization}
													history={history}
												>
													<CapacityMiddleware>
														<LazyWrapper>
															<EditIntegrationView
																organization={
																	organization
																}
															/>
														</LazyWrapper>
													</CapacityMiddleware>
												</Template>
											)}
										/>
										<Route
											exact
											path={`${path}/exploitation/integrations-old/:integrationId(\\d+)`}
											render={routeProps => (
												<Template
													organization={organization}
													history={history}
												>
													<CapacityMiddleware>
														<LazyWrapper>
															<EditIntegrationView
																organization={
																	organization
																}
															/>
														</LazyWrapper>
													</CapacityMiddleware>
												</Template>
											)}
										/>
										<Redirect
											to={`${path}/exploitation/integrations-old`}
										/>
									</Switch>
								)}
							/>
							<Route
								exact
								path={`${path}/custom-screens/templates`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<CustomScreens activeTab={0} />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/custom-screens/mappings`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<CustomScreens activeTab={1} />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/custom-screens/templates`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<SelectedOptionProvider>
												<LazyWrapper>
													<TemplatesList />
												</LazyWrapper>
											</SelectedOptionProvider>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/custom-screens/templates/new`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<SelectedOptionProvider>
												<LazyWrapper>
													<TemplateDetails />
												</LazyWrapper>
											</SelectedOptionProvider>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/custom-screens/templates/edit/:templateId`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<SelectedOptionProvider>
												<LazyWrapper>
													<TemplateDetails
														{...routeProps}
													/>
												</LazyWrapper>
											</SelectedOptionProvider>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/device-status`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<DeviceStatusList />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								path={`${path}/reports`}
								render={routeProps => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<ExportPage />
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
							<Route
								exact
								render={() => (
									<Template
										organization={organization}
										history={history}
									>
										<CapacityMiddleware>
											<LazyWrapper>
												<NotFoundPage
													title="access.common.error.labelNotFound"
													backHomeUrl={`/access/O${organization.id()}/checkpoints`}
												/>
											</LazyWrapper>
										</CapacityMiddleware>
									</Template>
								)}
							/>
						</Switch>
					</div>
				</ValidationStepProvider>
			</PollingProvider>
		</GoFastExportProvider>
	);
};

OrganizationRoutes.propTypes = {
	path: PropTypes.string.isRequired,
	organization: PropTypes.object.isRequired
};

export default function OrganizationRouteur({ match }) {
	const organizationId = match.params.organizationId;
	const path = `/access/O${organizationId}`;

	const currentOrganizationId = React.useRef(null);
	const hasAttemptedLoad = React.useRef(false);

	const { organization, isLoading, error } = useSelector(selectOrganization);

	const isValidOrganizationId = (() => {
		if (!organizationId) return false;
		return /^\d+$/.test(String(organizationId));
	})();

	React.useEffect(() => {
		if (!isValidOrganizationId) {
			hasAttemptedLoad.current = false;
			return;
		}
		if (organizationId !== currentOrganizationId.current) {
			currentOrganizationId.current = organizationId;
			getOneOrganization(organizationId); // this is an "auto-dispatching" redux action, I don't get it either, we'll clean this mess later.
		}
	}, [organizationId, isValidOrganizationId]);

	if (
		error &&
		error.code === 403 &&
		!isLoading &&
		organizationId === currentOrganizationId.current
	) {
		return (
			<PermissionsContext.Provider value={null}>
				<UnauthorizedPage errorCode={403} refresh={false} />
			</PermissionsContext.Provider>
		);
	}

	if (isLoading || !organization) {
		return <Loader />;
	}

	return (
		<RouterContext.Provider value={{ path }}>
			<OrganizationContext.Provider value={organization}>
				<OrganizationRoutes path={path} organization={organization} />
			</OrganizationContext.Provider>
		</RouterContext.Provider>
	);
}

OrganizationRouteur.propTypes = {
	match: PropTypes.shape({
		url: PropTypes.string.isRequired,
		params: PropTypes.shape({
			organizationId: PropTypes.oneOfType([
				PropTypes.number,
				PropTypes.string
			]).isRequired
		}).isRequired
	}).isRequired
};
